import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection/WebViewDetails.js";
import { HasExternalUIElements } from "../player/Player.js";
import { isAnyUnmutable, isStronglyUnmutable, neverUnmute, UnmuteInfo, UnmutePolicy, UserGestureUnmuter } from "./UserGestureUnmuter.js";

/**
 * Interface for handling unmute actions related to IMA ads.
 *
 * Implementations of this interface provide hooks for managing mute state
 * and user gestures before ad playback starts.
 */
export interface IMAUnmuter {
    beforeAdStart(manager: google.ima.AdsManager, info: google.ima.Ad): void;
    declareMute(): boolean;
    afterAdEnded(): void;
}

const AUDIO_ON_VAST_WRAPPER_ID_PREFIX = "cp908712032124";

const AUDIO_ON_VIEWABILITY_TIMEOUT = 6000;

const EXTERNAL_UI_ENABLE_SAFETY_TIMEOUT = 6000;


const UNMUTE_VOLUME_VALUE_DESKTOP = 0.1;

/**
 * Implements the {@link IMAUnmuter} interface to manage video mute state
 * in response to IMA ad events and external UI elements.
 *
 * This class follows the video element's mute status and can be used to
 * synchronize ad playback behavior with the user's mute preference.
 *
 * @remarks
 * - The constructor requires an HTMLVideoElement and an object implementing {@link HasExternalUIElements}.
 * - The {@link beforeAdStart} method is intended to be called before an ad starts, allowing for custom logic.
 * - The {@link declareMute} method returns the current mute state of the video element.
 *
 * @example
 * ```typescript
 * const unmuter = new IMAFollowVideoStatusUnmuter(videoElement, externalUI);
 * if (unmuter.declareMute()) {
 *   // Handle muted state
 * }
 * ```
 */
export class IMAFollowVideoStatusUnmuter implements IMAUnmuter {

    constructor(private video: HTMLVideoElement, private externalUIElements: HasExternalUIElements) {

    }

    beforeAdStart(manager: google.ima.AdsManager, info: google.ima.Ad): void {

    }

    afterAdEnded() : void {

    }
    declareMute(): boolean {
        return this.video.muted;
    }

}


/**
 * Configuration options for the IMA user gesture unmuter.
 *
 * @property seekOnlyForTrueviewInDefaultPolicy - If true, seeking is only allowed for TrueView ads in the default policy.
 * @property policies - An object containing unmute policies. The `defaultPolicy` key specifies the default unmute policy,
 * and additional keys can be used to define custom policies for specific cases.
 */
export type IMAUnmuterConfig = {
    seekOnlyForTrueviewInDefaultPolicy: boolean,
    policies: {
        defaultPolicy: UnmutePolicy,
        [k: string]: UnmutePolicy
    }
};



/**
 * Standard configuration for the IMA user gesture unmuter.
 *
 * @remarks
 * This configuration defines the default and specific unmute policies for handling audio in VAST wrappers.
 * It also specifies whether seeking is allowed only for TrueView ads under the default policy.
 *
 * @property policies - An object mapping policy identifiers to their respective unmute functions.
 *   - `defaultPolicy`: Uses `isStronglyUnmutable` to determine if unmuting is allowed.
 *   - `[AUDIO_ON_VAST_WRAPPER_ID_PREFIX]`: Uses `isAnyUnmutable` for VAST wrapper audio policies.
 * @property seekOnlyForTrueviewInDefaultPolicy - If `true`, seeking is restricted to TrueView ads when using the default policy.
 */
export const IMAUnmuterStandardConfig: IMAUnmuterConfig = {
    policies: {
        defaultPolicy: isStronglyUnmutable,
        [AUDIO_ON_VAST_WRAPPER_ID_PREFIX]: isAnyUnmutable
    },
    seekOnlyForTrueviewInDefaultPolicy: true
}


/**
 * Configuration object for the IMA user gesture unmuter in conservative mode.
 *
 * - `policies`: Defines unmute policies for different scenarios.
 *   - `defaultPolicy`: Uses `neverUnmute`, ensuring audio is never unmuted by default.
 *   - `[AUDIO_ON_VAST_WRAPPER_ID_PREFIX]`: Uses `isAnyUnmutable`, applying a specific policy for VAST wrapper IDs.
 * - `seekOnlyForTrueviewInDefaultPolicy`: If `false`, seeking is not restricted to TrueView ads under the default policy.
 */
export const IMAUnmuterConservativeConfig: IMAUnmuterConfig = {
    policies: {
        defaultPolicy: neverUnmute,
        [AUDIO_ON_VAST_WRAPPER_ID_PREFIX]: isAnyUnmutable
    },
    seekOnlyForTrueviewInDefaultPolicy: false
}

/**
 * Configuration object for the IMA user gesture unmuter.
 *
 * @remarks
 * This config defines the unmute policies for IMA playback scenarios.
 * The `policies` property maps policy identifiers to their respective unmute logic functions.
 * The `seekOnlyForTrueviewInDefaultPolicy` flag controls whether seeking is restricted to TrueView ads under the default policy.
 *
 * @property policies - An object mapping policy identifiers to unmute logic functions.
 * @property seekOnlyForTrueviewInDefaultPolicy - If `true`, seeking is only allowed for TrueView ads under the default policy.
 */
export const IMAUnmuterAnyConfig: IMAUnmuterConfig = {
    policies: {
        defaultPolicy: isAnyUnmutable,
        [AUDIO_ON_VAST_WRAPPER_ID_PREFIX]: isAnyUnmutable
    },
    seekOnlyForTrueviewInDefaultPolicy: false
}

/**
 * Specifies the policy for hiding the ad close button.
 *
 * - `NEVER_HIDE`: The close button is never hidden.
 * - `HIDE_ONLY_FOR_SPECIFIC_ADS`: The close button is hidden only for specific ads.
 * - `HIDE_ALWAYS`: The close button is always hidden.
 */
export enum AdCloseButtonHidePolicy {

    NEVER_HIDE = "never",
    HIDE_ONLY_FOR_SPECIFIC_ADS = "specific_only",
    HIDE_ALWAYS = 2


}

/**
 * Implements advanced unmute logic for IMA ads, handling user gesture requirements,
 * ad-specific policies, and UI interactions for ad close buttons.
 *
 * This class wraps a {@link UserGestureUnmuter} and coordinates unmuting the video element
 * based on ad policies, device type, and ad events from the IMA SDK. It also manages the
 * visibility and enablement of external UI elements (such as close buttons) according to
 * ad progress and completion events.
 *
 * @remarks
 * - Supports ad-specific unmute policies via wrapper ad IDs.
 * - Handles deferred unmute logic if the policy requires waiting for a condition.
 * - Manages UI elements for ad close actions, with configurable hide policies.
 * - Adjusts video volume and mute state for desktop and mobile environments.
 *
 * @param wrappedUnmuter - The underlying {@link UserGestureUnmuter} instance.
 * @param videoEl - The HTML video element to control.
 * @param externalUIElements - Provides access to external UI elements for ad controls.
 * @param deviceDetect - Device detection utility for platform-specific logic.
 * @param config - Configuration for unmute policies and behavior.
 * @param hidePolicy - Policy for hiding or disabling ad close buttons.
 *
 * @public
 */
export class IMAAdvancedUnmuter implements IMAUnmuter {


    private endEventsOfInterest = [
        google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
        google.ima.AdEvent.Type.USER_CLOSE,
        google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
        google.ima.AdEvent.Type.SKIPPED,
        google.ima.AdEvent.Type.COMPLETE
    ]

    private progressEvent = [
        google.ima.AdEvent.Type.AD_PROGRESS,
        google.ima.AdEvent.Type.IMPRESSION
    ]


    private wrapperIdsKeys: string[];

    private startEventsOfInterst = [
        google.ima.AdEvent.Type.STARTED,
        // google.ima.AdEvent.Type.RESUMED
        // google.ima.AdEvent.Type.AD_PROGRESS
    ]

    constructor(private wrappedUnmuter: UserGestureUnmuter,
        private videoEl: HTMLVideoElement,
        private externalUIElements: HasExternalUIElements,
        private deviceDetect: DetectedWebView,
        private config: IMAUnmuterConfig = IMAUnmuterStandardConfig,
        private hidePolicy: AdCloseButtonHidePolicy = AdCloseButtonHidePolicy.HIDE_ALWAYS
    ) {

        this.wrapperIdsKeys = Object.keys(this.config.policies).filter(e => e != "defaultPolicy");

    }




    private uiCycleProperty(manager: google.ima.AdsManager, endTrigger: "impression" | "viewtime", action: "hide" | "disable", viewTimeTrigger: number = AUDIO_ON_VIEWABILITY_TIMEOUT) {

        //gestione h/s (x) audio-on camp.
        const ext = this.externalUIElements.getCloseExternalUIElement();
        if (ext != null) {
            if (action == "hide") ext.show(false);
            ext.enabled(false);
        }

        let xShowCallback: (evt: google.ima.AdEvent) => void = null;
        let progressCallback: (evt: google.ima.AdEvent) => void = null;
        let completable: boolean = false;
        let safetyTimeoutId: number | null = null;

        const showAndUnregister = () => {
            const ext2 = this.externalUIElements.getCloseExternalUIElement();
            if (ext2 != null) {
                if (action == "hide") ext2.show(true);
                ext2.enabled(true);
            }
            if (xShowCallback) {
                this.endEventsOfInterest.forEach((evt) => {
                    manager.removeEventListener(evt, xShowCallback);
                });
            }
            if (progressCallback) {
                this.progressEvent.forEach(evt => {
                    manager.removeEventListener(evt, progressCallback);
                });
            }
            if (safetyTimeoutId != null) {
                window.clearTimeout(safetyTimeoutId);
                safetyTimeoutId = null;
            }
        }

        xShowCallback = () => {
            showAndUnregister();
        }
        progressCallback = (evt: google.ima.AdEvent) => {


            const currentTime: number = evt.getAdData()?.currentTime;
            const duration: number = evt.getAdData()?.duration;

            if (completable && endTrigger == "impression" && evt.type == google.ima.AdEvent.Type.IMPRESSION) {
                showAndUnregister();
            }

            if (completable && !(typeof currentTime == "undefined") && !(typeof duration == "undefined")) {

                if ((currentTime * 1000) >= viewTimeTrigger) {
                    showAndUnregister();
                }
            }
        }


        if (xShowCallback) {
            this.endEventsOfInterest.forEach((evt) => {
                manager.addEventListener(evt, xShowCallback);
            });
        }
        if (progressCallback) {
            this.progressEvent.forEach((evt) => {
                manager.addEventListener(evt, progressCallback);
            });
            const dostart = () => {
                completable = true;
                this.startEventsOfInterst.forEach((evt) => {
                    manager.removeEventListener(evt, dostart);
                })
            }
            this.startEventsOfInterst.forEach((evt) => {
                manager.addEventListener(evt, dostart);
            })
        }

        // Safety net: if the ad never renders (no STARTED/IMPRESSION/end events fire),
        // force-enable the external UI element so the user can still close/interact.
        safetyTimeoutId = window.setTimeout(() => {
            console.log("[AGGRO] safety timeout: force-enabling external UI element");
            showAndUnregister();
        }, EXTERNAL_UI_ENABLE_SAFETY_TIMEOUT);

    }


    afterAdEnded(): void {
        this.wrappedUnmuter.ensureGainMedia();
    }

    beforeAdStart(manager: google.ima.AdsManager, info: google.ima.Ad): void {


        let shouldUnmute = !this.videoEl.muted;
        // console.log("[AGGRO] is video el um?", shouldUnmute);

        let retrievedAdSpecificPolicy: UnmutePolicy = null;
        let specificPolicyKey: string = null;
        if (info && info.getWrapperAdIds()) {
            const maybeKey = this.wrapperIdsKeys.find(key => info.getWrapperAdIds().findIndex(wrap => wrap.startsWith(key)) >= 0);
            if (maybeKey && maybeKey in this.config.policies) {
                retrievedAdSpecificPolicy = this.config.policies[maybeKey];
                specificPolicyKey = maybeKey;
            }
        }

        let deferredPolicy: UnmutePolicy = null;
        if (!shouldUnmute && this.config.policies.defaultPolicy(this.wrappedUnmuter.currentUnmuteStatus())) {
            if (this.config.seekOnlyForTrueviewInDefaultPolicy) {
                const adSystem = info.getAdSystem();
                const wrappedAdSystems = info.getWrapperAdSystems();
                const skipTime = info.getSkipTimeOffset();
                const adexRegex = /(adsense|adex)/i;
                console.log("[AGGRO] seeking for tw", adSystem, wrappedAdSystems, skipTime);
                if (adSystem && adSystem.match(adexRegex) && skipTime > 0) {
                    console.log("[AGGRO] enabiling on tw");
                    shouldUnmute = true;
                }
            }
            else {
                // console.log("[AGGRO] um by default policy", this.wrappedUnmuter.currentUnmuteStatus());
                shouldUnmute = true;
            }
        }

        if (!shouldUnmute) {


            if (retrievedAdSpecificPolicy) {

                shouldUnmute = retrievedAdSpecificPolicy(this.wrappedUnmuter.currentUnmuteStatus());
                if (!shouldUnmute) {
                    console.log("[AGGRO] Deferring on", specificPolicyKey);
                    deferredPolicy = retrievedAdSpecificPolicy;
                }
                else {
                    // console.log("[AGGRO] UM on", specificPolicyKey, this.wrappedUnmuter.currentUnmuteStatus());
                }
            }


        }


        if (this.hidePolicy == AdCloseButtonHidePolicy.HIDE_ALWAYS ||
            (this.hidePolicy == AdCloseButtonHidePolicy.HIDE_ONLY_FOR_SPECIFIC_ADS && specificPolicyKey)) {
            if (specificPolicyKey) this.uiCycleProperty(manager, "viewtime", "hide", 2500);
            else this.uiCycleProperty(manager, "impression", "disable");
        }

        if (shouldUnmute || deferredPolicy) {
            const wasMuted = this.videoEl.muted;
            const previousVolume = this.videoEl.volume;
            const isDesktop = this.deviceDetect?.device?.other;
            const applyVolume = isDesktop ? UNMUTE_VOLUME_VALUE_DESKTOP : this.wrappedUnmuter.gainLevel;

            // console.log("[AGGRO] wasmuted", wasMuted, "previousvolume", this.videoEl.volume, "applyvolume", applyVolume);

            const unmuteExec = () => {
                this.videoEl.muted = false;
                // console.log("[AGGRO] manager.setVolume", applyVolume);
                manager.setVolume(applyVolume);
                if (isDesktop && wasMuted) {
                    this.videoEl.volume = applyVolume;
                }

                // // AGGIUNGI: Listener per aggiornare il volume quando gainRemoved diventa true
                // const onGainRemoved = (evt: Event, uinfo: UnmuteInfo) => {
                //     if (uinfo.gainRemoved) {
                //         const newVolume = this.wrappedUnmuter.gainLevel; // Ora sarà 1
                //         console.log("[AGGRO] Gain rimosso durante ad, aggiorno volume a", newVolume);
                //         manager.setVolume(newVolume);
                //         if (isDesktop) {
                //             this.videoEl.volume = newVolume;
                //         }
                //         // Rimuovi il listener dopo averlo usato
                //         this.wrappedUnmuter.offUnmuteConditionChange(onGainRemoved);
                //     }
                // };

                // // Registra il listener
                // this.wrappedUnmuter.onUnmuteConditionChange(onGainRemoved);

                // // Cleanup quando l'ad finisce
                // const cleanupListener = () => {
                //     this.wrappedUnmuter.offUnmuteConditionChange(onGainRemoved);
                //     this.endEventsOfInterest.forEach((evt) => {
                //         manager.removeEventListener(evt, cleanupListener);
                //     });
                // };
                // this.endEventsOfInterest.forEach((evt) => {
                //     manager.addEventListener(evt, cleanupListener);
                // });

                this.startEventsOfInterst.forEach((evt) => {
                    manager.removeEventListener(evt, startCallback);
                });
            }

            const deferredListener = (evt: Event, uinfo: UnmuteInfo) => {
                if (deferredPolicy && deferredPolicy(uinfo)) {
                    // console.log("[AGGRO] unmuting on deferred policy");
                    unmuteExec();
                    this.wrappedUnmuter.offUnmuteConditionChange(deferredListener);
                }
            }

            const startCallback = () => {
                if (shouldUnmute) unmuteExec();
                else if (deferredPolicy) {
                    // console.log("[AGGRO] deferring policy");
                    this.wrappedUnmuter.onUnmuteConditionChange(deferredListener);
                }
            }

            this.startEventsOfInterst.forEach((evt) => {
                manager.addEventListener(evt, startCallback);
            });

            if (wasMuted || isDesktop) {
                const endCallback = () => {
                    this.videoEl.muted = wasMuted;
                    if (deferredListener) this.wrappedUnmuter.offUnmuteConditionChange(deferredListener);
                    if (isDesktop) {
                        this.videoEl.volume = previousVolume;
                    }
                    this.endEventsOfInterest.forEach((evt) => {
                        manager.removeEventListener(evt, endCallback);
                    });
                }
                this.endEventsOfInterest.forEach((evt) => {
                    manager.addEventListener(evt, endCallback);
                })
            }
        }
    }


    declareMute(): boolean {
        return this.videoEl.muted && !this.config.policies.defaultPolicy(this.wrappedUnmuter.currentUnmuteStatus());
    }


}